// src/app/router.ts: the SPA router (spec 2.3, 2.4). No router library: a table of patterns, a signal
// holding the current match, and History API navigation.
import { signal, type ReadonlySignal } from "@preact/signals";
import { h } from "preact";

export type RouteName =
  | "root" | "signin" | "continue" | "unsubscribe"
  | "home" | "rooms" | "room" | "item" | "unsorted" | "sort" | "messages" | "people" | "settings"
  | "team" | "teamProject" | "teamProjectTab" | "teamIdeas" | "teamHomes" | "teamStartYourBuild" | "teamSettings" | "teamSetup" | "teamActivate" | "teamActivateDone"
  | "demo" | "demoOutbox" | "notFound"
  // docs/DECISIONS.md additions (placeholders until their screens land) and the dev and demo UI specimen
  | "teamPipeline" | "teamMessages" | "teamHome" | "start" | "uiSpecimen"
  // Start Your Build (docs/onsite/ONSITE.md 4.4, 4.5): the sent pages and "It wasn't me"
  | "startSent" | "notMe"
  // The builder-workspace privacy notice (docs/DECISIONS.md "Owner answers")
  | "privacy"
  // Get the app: the link target for email footers and QR cards (docs/DECISIONS.md "Install prompt")
  | "install"
  // Big Choices: the run through the big questions about the house (docs/DECISIONS.md "Big Choices")
  | "choices";
export type RouteMatch = { name: RouteName; params: Record<string, string>; query: URLSearchParams; hash: string; path: string };

/** The team project tabs that `/team/p/:pid/:tab` accepts. */
export const TEAM_TABS = ["rooms", "photos", "messages", "activity", "people", "facts"] as const;

/** Patterns in the spec 2.3 table order, then the docs/DECISIONS.md additions. `notFound` has no pattern. */
export const ROUTE_PATTERNS: readonly (readonly [Exclude<RouteName, "notFound">, string])[] = [
  ["root", "/"],
  ["signin", "/signin"],
  ["continue", "/auth/continue"],
  ["unsubscribe", "/unsubscribe"],
  ["home", "/p/:pid"],
  ["rooms", "/p/:pid/rooms"],
  ["item", "/p/:pid/rooms/:rid/i/:iid"],
  ["room", "/p/:pid/rooms/:rid"],
  ["unsorted", "/p/:pid/unsorted"],
  ["sort", "/p/:pid/sort"],
  ["messages", "/p/:pid/messages"],
  ["people", "/p/:pid/people"],
  ["settings", "/p/:pid/settings"],
  ["team", "/team"],
  ["teamProject", "/team/p/:pid"],
  ["teamIdeas", "/team/ideas"],
  ["teamHomes", "/team/homes"],
  ["teamStartYourBuild", "/team/start-your-build"],
  ["teamSettings", "/team/settings"],
  // Activate: a builder in preview pays and opens (docs/DECISIONS.md "Activate is not part of setup (owner, 2026-10-22)")
  ["teamActivate", "/team/activate"],
  // After Activate: the builder is open to families (src/setup/ActivateDone.tsx)
  ["teamActivateDone", "/team/activate/done"],
  // A new builder's setup (docs/DECISIONS.md "Builder setup"): /team/setup, then /team/setup/<step>
  ["teamSetup", "/team/setup"],
  ["teamSetup", "/team/setup/:step"],
  ["teamProjectTab", "/team/p/:pid/:tab"],
  ["demo", "/demo"],
  ["demoOutbox", "/demo/outbox/:id"],
  ["teamPipeline", "/team/pipeline"],
  // The builder's Home (a live or paused builder's landing; in preview it sends people to /team, the setup workspace).
  ["teamHome", "/team/home"],
  // The builder's Messages: every family's conversation. /team/messages is the list (desktop: opens the newest), /team/messages/:pid one thread.
  ["teamMessages", "/team/messages"],
  ["teamMessages", "/team/messages/:pid"],
  ["start", "/start"],
  ["uiSpecimen", "/__ui"],
  ["startSent", "/start/sent"],
  ["notMe", "/auth/not-me"],
  ["privacy", "/privacy"],
  ["install", "/install"],
  ["choices", "/p/:pid/choices"],
];

const PARAM_VALUE = /^[A-Za-z0-9_.-]{1,64}$/;

function segments(path: string): string[] {
  return path.split("/").filter(Boolean);
}

/** Pure matcher: the route name and params for a pathname (without query or hash). */
export function matchPath(pathname: string): { name: RouteName; params: Record<string, string> } {
  let parts: string[];
  try {
    parts = segments(pathname).map((s) => decodeURIComponent(s));
  } catch {
    return { name: "notFound", params: {} };
  }
  for (const [name, pattern] of ROUTE_PATTERNS) {
    const pat = segments(pattern);
    if (pat.length !== parts.length) continue;
    const params: Record<string, string> = {};
    let ok = true;
    for (let i = 0; i < pat.length; i++) {
      const p = pat[i]!;
      const v = parts[i]!;
      if (p.startsWith(":")) {
        if (!PARAM_VALUE.test(v)) {
          ok = false;
          break;
        }
        params[p.slice(1)] = v;
      } else if (p !== v) {
        ok = false;
        break;
      }
    }
    if (!ok) continue;
    if (name === "teamProjectTab" && !(TEAM_TABS as readonly string[]).includes(params.tab ?? "")) continue;
    return { name, params };
  }
  return { name: "notFound", params: {} };
}

function currentMatch(): RouteMatch {
  const { pathname, search, hash } = window.location;
  const { name, params } = matchPath(pathname);
  return { name, params, query: new URLSearchParams(search), hash: hash.startsWith("#") ? hash.slice(1) : hash, path: pathname };
}

const routeSignal = signal<RouteMatch>(currentMatch());
export const route: ReadonlySignal<RouteMatch> = routeSignal;

let listening = false;
function listen(): void {
  if (listening || typeof window === "undefined") return;
  listening = true;
  window.addEventListener("popstate", () => {
    routeSignal.value = currentMatch();
  });
}
listen();

/** Push (default) or replace history. Scrolls to top unless scroll === "preserve". */
export function navigate(to: string, opts?: { replace?: boolean; scroll?: "top" | "preserve" }): void {
  const target = new URL(to, window.location.href);
  if (target.origin !== window.location.origin) {
    window.location.assign(target.href);
    return;
  }
  const rel = target.pathname + target.search + target.hash;
  if (opts?.replace) window.history.replaceState(null, "", rel);
  else window.history.pushState(null, "", rel);
  routeSignal.value = currentMatch();
  if (opts?.scroll !== "preserve") window.scrollTo(0, 0);
}

/** Build a path from a route name. Throws on a missing param. */
export function href(name: RouteName, params?: Record<string, string>, query?: Record<string, string>): string {
  if (name === "notFound") throw new Error("href: notFound has no path");
  // A name with two patterns (teamSetup: with and without its step) takes the one whose params are all given.
  const entries = ROUTE_PATTERNS.filter(([n]) => n === name);
  const given = (pattern: string) => segments(pattern).every((seg) => !seg.startsWith(":") || !!params?.[seg.slice(1)]);
  const entry = entries.length > 1 ? (entries.filter(([, p]) => given(p)).pop() ?? entries[0]) : entries[0];
  if (!entry) throw new Error(`href: unknown route ${name}`);
  const path =
    "/" +
    segments(entry[1])
      .map((seg) => {
        if (!seg.startsWith(":")) return seg;
        const key = seg.slice(1);
        const value = params?.[key];
        if (value === undefined || value === "") throw new Error(`href: missing param ${key} for ${name}`);
        return encodeURIComponent(value);
      })
      .join("/");
  const qs = query ? new URLSearchParams(query).toString() : "";
  return qs ? `${path}?${qs}` : path;
}

type LinkProps = { to: string; class?: string; replace?: boolean; children?: preact.ComponentChildren; [attr: string]: unknown };

/** <a> that routes client-side for same-origin, unmodified left clicks. */
export function Link(props: LinkProps): preact.JSX.Element {
  const { to, replace, children, ...rest } = props;
  const onClick = (e: MouseEvent) => {
    const userOnClick = rest.onClick as ((e: MouseEvent) => void) | undefined;
    userOnClick?.(e);
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const target = (e.currentTarget as HTMLAnchorElement | null)?.getAttribute("target");
    if (target && target !== "_self") return;
    const url = new URL(to, window.location.href);
    if (url.origin !== window.location.origin) return;
    e.preventDefault();
    navigate(url.pathname + url.search + url.hash, replace ? { replace: true } : undefined);
  };
  return h("a", { ...rest, href: to, onClick }, children);
}
